import React, { useState } from "react";
import { Reveal } from "../../lib/motion";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import axios from "axios";
import { isValidPhoneNumber } from "libphonenumber-js";
import { useApp } from "@/context/AppContext";
import InternationalPhoneInput from "@/components/site/InternationalPhoneInput";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const labelCls =
  "block text-[11px] tracking-[0.22em] uppercase font-bold text-black/75 mb-2";
const inputBase =
  "w-full h-12 px-4 rounded-xl bg-white/80 border text-black placeholder:text-black/40 focus:outline-none transition-colors font-medium";
const errCls =
  "mt-1.5 text-[12px] leading-tight text-[#C1350B] font-semibold";

function Field({ id, label, testid, required, error, className = "", children }) {
  return (
    <div className={className}>
      <label htmlFor={id} className={labelCls} data-testid={`${testid}-label`}>
        {label}
      </label>
      {children}
      {error && (
        <p
          className={errCls}
          role="alert"
          id={`${id}-error`}
          data-testid={`${testid}-error`}
        >
          {error}
        </p>
      )}
    </div>
  );
}

const INTEREST_OPTIONS = [
  { key: "WEBSITE",         label: "Website" },
  { key: "CRM",             label: "CRM" },
  { key: "LANDING_PAGE",    label: "Landing Page" },
  { key: "ORANGE_OPTIMISE", label: "Orange Optimise" },
  { key: "ORANGE_CARE",     label: "OrangeCare" },
  { key: "OTHER",           label: "Other" },
];

export default function CrmDiscovery() {
  const { t, currency, language } = useApp();
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    full_name: "",
    business_name: "",
    email: "",
    phone: "",
    industry: "",
    company_size: "",
    current_website: "",
    current_stack: "",
    needs: [],
    problem: "",
    interests: [],
  });

  const options = t("discovery.needs_options") || [];
  const on = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };
  const toggle = (v) =>
    setForm((f) => ({
      ...f,
      needs: f.needs.includes(v) ? f.needs.filter((x) => x !== v) : [...f.needs, v],
    }));

  const toggleInterest = (k) =>
    setForm((f) => {
      const has = f.interests.includes(k);
      return { ...f, interests: has ? f.interests.filter((x) => x !== k) : [...f.interests, k] };
    });

  const validate = () => {
    const e = {};
    const req = t("discovery.err_field_required");
    if (!form.full_name.trim()) e.full_name = req;
    if (!form.business_name.trim()) e.business_name = req;
    if (!form.email.trim()) e.email = req;
    else if (!EMAIL_RE.test(form.email.trim()))
      e.email = t("discovery.err_email_invalid");
    if (!form.phone || !form.phone.trim()) e.phone = req;
    else if (!isValidPhoneNumber(form.phone.trim()))
      e.phone = t("discovery.err_phone_invalid") || "Please enter a valid phone number.";
    if (!form.industry.trim()) e.industry = req;
    if (!form.company_size.trim()) e.company_size = req;
    // Require at least one interest — dropdown replaced by multi-select pills.
    if (!form.interests.length) e.interests = "Please select at least one interest.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error(t("discovery.err_check"));
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/api/enquiries`, {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        business_name: form.business_name,
        website_type: "CRM Discovery",
        interests: form.interests,
        // Legacy compat — pick the first non-OTHER canonical interest, or
        // OTHER if that's the only thing selected. Backend prefers `interests`
        // when both are present.
        interested_in: form.interests.find((x) => x !== "OTHER") || form.interests[0] || null,
        industry: form.industry,
        company_size: form.company_size,
        current_url: form.current_website,
        description: form.current_stack,
        needs: form.needs,
        problem: form.problem,
        currency,
        language,
      });
      setSent(true);
      // Smooth-scroll the section into view under the sticky header — no jarring jump.
      window.setTimeout(() => {
        document.querySelector("#discovery")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    } catch (err) {
      toast.error(t("discovery.err_fail"));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = (name, extra = "") =>
    `${inputBase} ${
      errors[name]
        ? "border-[#C1350B] focus:border-[#C1350B]"
        : "border-black/15 focus:border-black"
    } ${extra}`;
  const textareaCls = (name) =>
    `w-full px-4 py-3 rounded-xl bg-white/80 border text-black placeholder:text-black/40 focus:outline-none transition-colors font-medium resize-none ${
      errors[name]
        ? "border-[#C1350B] focus:border-[#C1350B]"
        : "border-black/15 focus:border-black"
    }`;

  return (
    <section id="discovery" data-testid="discovery-section" className="relative py-20 md:py-28">
      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10">
        {sent ? (
          <Reveal>
            <div
              data-testid="discovery-success"
              className="rounded-3xl p-10 md:p-14 bg-white border border-black/10 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.3)] text-center"
            >
              <div className="w-16 h-16 rounded-full bg-black text-white grid place-items-center mx-auto">
                <Check size={26} strokeWidth={2.6} />
              </div>
              <h2
                data-testid="discovery-success-title"
                className="mt-6 font-display font-black text-4xl md:text-5xl text-black tracking-tight uppercase"
              >
                {t("discovery.success_title")}
              </h2>
              <p className="mt-4 text-black/80 text-base md:text-lg leading-relaxed max-w-xl mx-auto">
                {t("discovery.success_sub")}
              </p>
              <button
                data-testid="discovery-again"
                onClick={() => {
                  setSent(false);
                  setErrors({});
                  setForm({
                    full_name: "",
                    business_name: "",
                    email: "",
                    phone: "",
                    industry: "",
                    company_size: "",
                    current_website: "",
                    current_stack: "",
                    needs: [],
                    problem: "",
                  });
                }}
                className="mt-8 inline-flex items-center gap-2 h-12 px-7 rounded-full bg-black text-white font-display font-bold text-sm tracking-wide uppercase hover:bg-black/90 transition-all"
              >
                {t("discovery.success_again")}
              </button>
            </div>
          </Reveal>
        ) : (
          <>
        <Reveal>
          {/* Phase 3I.4O.2 — eyebrow copy update. */}
          <p className="text-[11px] tracking-[0.24em] uppercase text-black/75 mb-4 font-bold" data-testid="discovery-eyebrow">
            LET US ADD SOME JUICE!
          </p>
          <h2 className="font-display font-black text-black leading-[1.02] tracking-tight text-4xl md:text-5xl lg:text-6xl">
            Let's build <span className="text-black/55">your Orange system.</span>
          </h2>
          <p className="mt-5 max-w-2xl text-black/80 text-base md:text-lg leading-relaxed">
            {t("discovery.sub")}
          </p>
        </Reveal>

          <Reveal>
            <form
              data-testid="discovery-form"
              onSubmit={submit}
              noValidate
              className="mt-10 rounded-3xl p-6 md:p-10 bg-white/70 backdrop-blur border border-black/10 space-y-5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-5">
                <Field
                  id="disc-name"
                  testid="discovery-name"
                  label={t("discovery.f_name")}
                  error={errors.full_name}
                >
                  <input
                    id="disc-name"
                    data-testid="discovery-name"
                    placeholder={t("discovery.ph_name")}
                    value={form.full_name}
                    onChange={on("full_name")}
                    aria-invalid={!!errors.full_name}
                    aria-describedby={errors.full_name ? "disc-name-error" : undefined}
                    className={inputCls("full_name")}
                  />
                </Field>

                <Field
                  id="disc-business"
                  testid="discovery-business"
                  label={t("discovery.f_business")}
                  error={errors.business_name}
                >
                  <input
                    id="disc-business"
                    data-testid="discovery-business"
                    placeholder={t("discovery.ph_business")}
                    value={form.business_name}
                    onChange={on("business_name")}
                    aria-invalid={!!errors.business_name}
                    aria-describedby={errors.business_name ? "disc-business-error" : undefined}
                    className={inputCls("business_name")}
                  />
                </Field>

                <Field
                  id="disc-email"
                  testid="discovery-email"
                  label={t("discovery.f_email")}
                  error={errors.email}
                >
                  <input
                    id="disc-email"
                    data-testid="discovery-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder={t("discovery.ph_email")}
                    value={form.email}
                    onChange={on("email")}
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "disc-email-error" : undefined}
                    className={inputCls("email")}
                  />
                </Field>

                <Field
                  id="disc-phone"
                  testid="discovery-phone"
                  label={t("discovery.f_phone")}
                  error={errors.phone}
                >
                  <InternationalPhoneInput
                    id="disc-phone"
                    testid="discovery-phone"
                    value={form.phone}
                    onChange={(v) => {
                      setForm((f) => ({ ...f, phone: v || "" }));
                      if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
                    }}
                    defaultCountry="GB"
                    placeholder={t("discovery.ph_phone")}
                    invalid={!!errors.phone}
                    ariaDescribedBy={errors.phone ? "disc-phone-error" : undefined}
                  />
                </Field>

                <Field
                  id="disc-industry"
                  testid="discovery-industry"
                  label={t("discovery.f_industry")}
                  error={errors.industry}
                >
                  <input
                    id="disc-industry"
                    data-testid="discovery-industry"
                    placeholder={t("discovery.ph_industry")}
                    value={form.industry}
                    onChange={on("industry")}
                    aria-invalid={!!errors.industry}
                    aria-describedby={errors.industry ? "disc-industry-error" : undefined}
                    className={inputCls("industry")}
                  />
                </Field>

                <Field
                  id="disc-size"
                  testid="discovery-size"
                  label={t("discovery.f_size")}
                  error={errors.company_size}
                >
                  <input
                    id="disc-size"
                    data-testid="discovery-size"
                    placeholder={t("discovery.ph_size")}
                    value={form.company_size}
                    onChange={on("company_size")}
                    aria-invalid={!!errors.company_size}
                    aria-describedby={errors.company_size ? "disc-size-error" : undefined}
                    className={inputCls("company_size")}
                  />
                </Field>

                <Field
                  id="disc-current"
                  testid="discovery-current"
                  label={t("discovery.f_current")}
                  className="md:col-span-2"
                >
                  <input
                    id="disc-current"
                    data-testid="discovery-current"
                    inputMode="url"
                    autoComplete="url"
                    placeholder={t("discovery.ph_current")}
                    value={form.current_website}
                    onChange={on("current_website")}
                    className={inputCls("current_website")}
                  />
                </Field>

                <Field
                  id="disc-stack"
                  testid="discovery-stack"
                  label={t("discovery.f_stack")}
                  className="md:col-span-2"
                >
                  <input
                    id="disc-stack"
                    data-testid="discovery-stack"
                    placeholder={t("discovery.ph_stack")}
                    value={form.current_stack}
                    onChange={on("current_stack")}
                    className={inputCls("current_stack")}
                  />
                </Field>
              </div>

              <div>
                <p className={labelCls}>{t("discovery.f_needs")}</p>
                <div className="flex flex-wrap gap-2">
                  {options.map((o) => {
                    const selected = form.needs.includes(o);
                    return (
                      <button
                        key={o}
                        type="button"
                        data-testid={`discovery-need-${o.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => toggle(o)}
                        className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[13px] font-semibold transition-all ${
                          selected
                            ? "bg-black text-white border border-black"
                            : "bg-white text-black border border-black/20 hover:border-black"
                        }`}
                      >
                        {selected && <Check size={13} strokeWidth={3} />}
                        {o}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <p className={labelCls}>What are you interested in? *</p>
                <div className="flex flex-wrap gap-2" data-testid="discovery-interests">
                  {INTEREST_OPTIONS.map(({ key, label }) => {
                    const selected = form.interests.includes(key);
                    return (
                      <button
                        key={key}
                        type="button"
                        data-testid={`discovery-interest-${key.toLowerCase()}`}
                        onClick={() => {
                          toggleInterest(key);
                          if (errors.interests) setErrors((prev) => ({ ...prev, interests: undefined }));
                        }}
                        aria-pressed={selected}
                        className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[13px] font-semibold transition-all ${
                          selected
                            ? "bg-black text-white border border-black"
                            : "bg-white text-black border border-black/20 hover:border-black"
                        }`}
                      >
                        {selected && <Check size={13} strokeWidth={3} />}
                        {label}
                      </button>
                    );
                  })}
                </div>
                {errors.interests && (
                  <p className={errCls} role="alert"
                     data-testid="discovery-interests-error">
                    {errors.interests}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="disc-problem" className={labelCls}>
                  {t("discovery.f_problem")}
                </label>
                <textarea
                  id="disc-problem"
                  data-testid="discovery-problem"
                  rows={4}
                  placeholder={t("discovery.ph_problem")}
                  value={form.problem}
                  onChange={on("problem")}
                  className={textareaCls("problem")}
                />
              </div>

              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-2">
                <p className="text-[12px] text-black/60">{t("discovery.agree")}</p>
                <button
                  data-testid="discovery-submit"
                  type="submit"
                  disabled={busy}
                  className="group inline-flex items-center gap-3 h-14 px-8 rounded-full bg-black text-white font-display font-bold text-sm tracking-wide uppercase hover:bg-black/90 transition-all disabled:opacity-60 shrink-0"
                >
                  {busy ? t("discovery.sending") : "Send"}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform rtl:-scale-x-100" />
                </button>
              </div>
            </form>
          </Reveal>
          </>
        )}
      </div>
    </section>
  );
}
